import api from './api';

/**
 * Service for product-related operations
 */
const productService = {
  /**
   * Get all products
   * @param {Object} options - Query options
   * @param {number} [options.limit=10] - Max number of results
   * @param {number} [options.offset=0] - Result offset
   * @param {boolean} [options.include_archived=false] - Include archived products
   * @returns {Promise<Object>} Products with pagination info
   */
  getAllProducts: async ({ limit = 10, offset = 0, include_archived = false } = {}) => {
    try {
      const response = await api.get('/products', {
        params: { limit, offset, include_archived }
      });
      
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get products');
    }
  },

  /**
   * Get product by ID
   * @param {string} id - Product ID
   * @returns {Promise<Object>} Product
   */
  getProductById: async (id) => {
    try {
      const response = await api.get(`/products/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to get product');
    }
  },

  /**
   * Create a new product
   * @param {Object} productData - The product data
   * @returns {Promise<Object>} The created product
   */
  createProduct: async (productData) => {
    try {
      const response = await api.post('/products', productData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create product');
    }
  },

  /**
   * Update an existing product
   * @param {string} id - Product ID
   * @param {Object} productData - The product data to update
   * @returns {Promise<Object>} The updated product
   */
  updateProduct: async (id, productData) => {
    try {
      const response = await api.put(`/products/${id}`, productData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update product');
    }
  },

  /**
   * Delete a product
   * @param {string} id - Product ID
   * @returns {Promise<Object>} Success message
   */
  deleteProduct: async (id) => {
    try {
      const response = await api.delete(`/products/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete product');
    }
  },

  /**
   * Archive a product
   * @param {string} id - Product ID
   * @returns {Promise<Object>} The archived product
   */
  archiveProduct: async (id) => {
    try {
      const response = await api.put(`/products/${id}/archive`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to archive product');
    }
  },

  /**
   * Restore an archived product
   * @param {string} id - Product ID
   * @returns {Promise<Object>} The restored product
   */
  restoreProduct: async (id) => {
    try {
      const response = await api.put(`/products/${id}/restore`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to restore product');
    }
  },

  /**
   * Sync products with Polar.sh
   * @returns {Promise<Object>} Sync result
   */
  syncWithPolar: async () => {
    try {
      const response = await api.post('/products/polar/sync');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to sync with Polar.sh');
    }
  },

  /**
   * Get ticket credit cost
   * @returns {Promise<Object>} Ticket credit cost
   */
  getTicketCreditCost: async () => {
    // Default to 1 credit per ticket
    return {
      success: true,
      creditCost: 1
    };
  }
};

export default productService;